import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MediaItem } from "./MaintenanceRowDetail";

/**
 * Image tile with hover overlay and modal preview.
 * Hover actions use <a> links to guarantee working "open in new tab" and "download" for blob URLs.
 */
export default function ImageTile({ item, dense }: { item: MediaItem; dense?: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          position: "relative",
          width: "100%",
          cursor: "zoom-in",
          borderRadius: 1,
          overflow: "hidden",
          img: {
            display: "block",
            width: "100%",
            height: "auto",
            maxHeight: dense ? "50vh" : 220,
            objectFit: "cover",
          },
          "&:hover .overlay": { opacity: 1 },
        }}
      >
        <img src={item.url} alt={item.filename || item.id} loading="lazy" />
        {/* Hover overlay */}
        <Box
          className="overlay"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            backgroundColor: theme => alpha(theme.palette.background.default, 0.35),
            backdropFilter: "blur(2px)",
            opacity: 0,
            transition: "opacity 120ms ease-in-out",
          }}
        >
          <Tooltip title={t("common.open") || "Open"} arrow>
            <IconButton
              size="small"
              component="a"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              sx={{
                bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t("common.download") || "Download"} arrow>
            <IconButton
              size="small"
              component="a"
              href={item.url}
              download={item.filename || "media"}
              onClick={e => e.stopPropagation()}
              sx={{
                bgcolor: theme => alpha(theme.palette.background.paper, 0.9),
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Modal preview */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogActions sx={{ justifyContent: "space-between", p: 1 }}>
          <Box />
          <Box>
            <Tooltip title={t("common.open") || "Open"} arrow>
              <IconButton component="a" href={item.url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.download") || "Download"} arrow>
              <IconButton component="a" href={item.url} download={item.filename || "media"}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("common.close") || "Close"} arrow>
              <IconButton onClick={() => setOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogActions>

        <DialogContent
          dividers
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: theme => alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Box
            component="img"
            src={item.url}
            alt={item.filename || item.id}
            sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
